// AYTM-12710: shared helpers for the multi-axis product family selector.
// See specs/questions/ecommerce/product-family.md §Variant Selector.

import { DimensionEntry, IEcommerceAnswerSchema } from '../types'

export type Axis = { name: string; values: string[] }
export type { DimensionEntry }

// AYTM-12710 OQ-WD-69: synthetic axis name for a legacy single-axis family that carries a
// dimension value but no dimension name. Only reachable from data launched/drafted before the
// named-axis model (branch authoring always sets a name). Members share this one key so they
// collapse onto a single axis and the value buttons still render; the selector hides the
// axis-name label for it (master rendered bare value tiles, no header). Using the value as the
// name instead would scatter each value onto its own one-button axis.
export const LEGACY_AXIS = '__legacy_variant__'

// Backward-compatible read of a product's axis assignments.
// Pre-migration data carries legacy single-axis scalars; the new shape carries an array.
export function readDimensions(product: IEcommerceAnswerSchema): DimensionEntry[] {
  if (product.dimensions && product.dimensions.length) return product.dimensions
  if (product.dimension_value) {
    return [{ name: product.dimension_name || LEGACY_AXIS, value: product.dimension_value }]
  }
  return []
}

// Build the family's axes from its member products. Caller must pass `family` in parent-first
// order; the parent's dimensions declaration wins the axis ordering and supplies the optional
// author-set `valueOrder` per axis (set via the editor family dialog). See spec §Axes.
export function buildAxes(family: IEcommerceAnswerSchema[]): Axis[] {
  const headValueOrder = new Map<string, string[]>()
  family[0]?.dimensions?.forEach(dim => {
    if (dim.name && dim.valueOrder) headValueOrder.set(dim.name, dim.valueOrder)
  })

  const order: string[] = []
  const valuesByName = new Map<string, string[]>()
  family.forEach(product => {
    readDimensions(product).forEach(dim => {
      if (!dim.name) return
      if (!valuesByName.has(dim.name)) {
        order.push(dim.name)
        valuesByName.set(dim.name, [])
      }
      const bucket = valuesByName.get(dim.name)!
      if (dim.value && !bucket.includes(dim.value)) bucket.push(dim.value)
    })
  })
  return order.map(name => ({ name, values: mergeAuthorValueOrder(valuesByName.get(name)!, headValueOrder.get(name)) }))
}

// Merge the author-set value order with the family's first-appearance values: author entries
// first (only those still present), then any values the family has gained since.
export function mergeAuthorValueOrder(seen: string[], explicit?: string[]): string[] {
  if (!explicit) return seen
  const known = new Set(seen)
  const explicitSet = new Set(explicit)
  return explicit.filter(v => known.has(v)).concat(seen.filter(v => !explicitSet.has(v)))
}

// Flat axis-name → value map for the given product.
export function selectionsOf(product: IEcommerceAnswerSchema): Record<string, string> {
  const out: Record<string, string> = {}
  readDimensions(product).forEach(d => {
    out[d.name] = d.value
  })
  return out
}

// Find a child whose dimensions cover all of the given (axis, value) selections.
export function findMatchingSKU(
  children: IEcommerceAnswerSchema[],
  targetSelections: Record<string, string>
): IEcommerceAnswerSchema | undefined {
  return children.find(product => {
    const dims = readDimensions(product)
    return Object.entries(targetSelections).every(([axisName, value]) =>
      dims.find(d => d.name === axisName && d.value === value)
    )
  })
}

// Resolve the SKU a respondent would land on when clicking `(axisName, value)` while keeping
// the current selections on other axes — falling back to any SKU carrying that axis value when
// the exact intersection does not exist (sparse matrix).
export function findSKUForValue(
  children: IEcommerceAnswerSchema[],
  currentSelections: Record<string, string>,
  axisName: string,
  value: string
): IEcommerceAnswerSchema | undefined {
  const target: Record<string, string> = { ...currentSelections, [axisName]: value }
  const exact = findMatchingSKU(children, target)
  if (exact) return exact
  return children.find(product => {
    const dim = readDimensions(product).find(d => d.name === axisName)
    return dim?.value === value
  })
}

// True iff `(axisName, value)` is reachable without changing any other axis selection.
export function isCombinationAvailable(
  children: IEcommerceAnswerSchema[],
  currentSelections: Record<string, string>,
  axisName: string,
  value: string
): boolean {
  const target: Record<string, string> = { ...currentSelections, [axisName]: value }
  return Boolean(findMatchingSKU(children, target))
}

// True iff `(value)` on axes[axisIndex] is reachable when matching only the selections on
// axes above it (axes 0..axisIndex-1). The first axis is always reachable (no upper constraints).
// Used by retailers that filter incompatible values (Walmart, Target) — see spec
// §Multi-Axis Selector Pattern §Per-retailer divergence.
export function isCompatibleWithUpperAxes(
  children: IEcommerceAnswerSchema[],
  axes: Axis[],
  currentSelections: Record<string, string>,
  axisIndex: number,
  value: string
): boolean {
  if (axisIndex === 0) return true
  const target: Record<string, string> = { [axes[axisIndex].name]: value }
  for (let i = 0; i < axisIndex; i += 1) {
    const upperName = axes[i].name
    const upperSel = currentSelections[upperName]
    if (upperSel) target[upperName] = upperSel
  }
  return Boolean(findMatchingSKU(children, target))
}
